/**
 * SubjectCompanyStrip — Centrum uprawnień przy OSOBIE: pasek „W tej firmie" nad uprawnieniami.
 *
 * Uprawnienia są per firma (każda firma ma własne role, reguły i menu). Firmę wybiera się z listy
 * rozwijanej w panelu bocznym — tak samo jak przy roli i zespole (decyzja 2026-09-25: zakładki
 * firm nad uprawnieniami wycofane, wróciła lista). Firma bez dostępu osoby jest na liście
 * wyszarzona.
 *
 * Pasek mówi, SKĄD osoba ma prawa w wybranej firmie: jej role (w kolejności — pierwsza główna)
 * i zespoły. To tylko podgląd — role i dostęp do firmy zmienia się na karcie użytkownika (jedno
 * miejsce nadawania ról, decyzja 2026-09-24), stąd link.
 */

import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ExternalLink } from 'lucide-react';

import { Badge } from '@/components/Badge';
import { useUserPermissions } from '@/lib/use-permissions';

export interface SubjectCompanyStripProps {
  userId: string;
  companies: Array<{ id: string }>;
  activeCompanyId: string | null;
  onSelect: (companyId: string) => void;
  /** Zespoły osoby w wybranej firmie (zespoły są per firma, jak role). */
  teams: Array<{ id: string; name: string }>;
  /** W trakcie edycji firma jest zamrożona — przełączenie wyrzuciłoby brudnopis. */
  disabled?: boolean;
}

export function SubjectCompanyStrip({
  userId,
  companies,
  activeCompanyId,
  onSelect,
  teams,
  disabled = false,
}: SubjectCompanyStripProps) {
  const { t } = useTranslation();

  // Ten sam odczyt (i ta sama pamięć podręczna) co karta użytkownika — zmiana ról na karcie
  // odświeża pasek tu bez przeładowania.
  const access = useUserPermissions(userId);

  const byCompany = new Map((access.data?.companies ?? []).map((a) => [a.companyId, a]));
  const activeAccess = activeCompanyId ? byCompany.get(activeCompanyId) : undefined;
  const roles = activeAccess?.roles ?? [];
  const firstAccessible = companies.find((c) => byCompany.has(c.id))?.id ?? null;

  // Wybrana firma bez dostępu (np. po przełączeniu z roli na osobę) — przeskocz na pierwszą,
  // w której osoba jest. Wyjątek: tryb edycji (firma zamrożona).
  useEffect(() => {
    if (disabled || !access.data || !firstAccessible) return;
    if (!activeCompanyId || !byCompany.has(activeCompanyId)) onSelect(firstAccessible);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- byCompany wynika z access.data
  }, [access.data, activeCompanyId, firstAccessible, disabled, onSelect]);

  if (!activeAccess) return null;

  return (
    <div
      className="mx-5 mt-3 flex flex-wrap items-center gap-2 rounded-lg bg-bg-secondary px-3 py-2 text-xs"
      data-testid="subject-company-strip"
    >
      <span className="text-text-muted">
        {t('tenant.permissions.center.companyTabs.inCompany')}
      </span>
      <span className="text-text-secondary">{t('tenant.permissions.center.subject.role')}:</span>
      {roles.length === 0 ? (
        <span className="text-text-muted">
          {t('tenant.permissions.center.companyTabs.noRoles')}
        </span>
      ) : (
        roles.map((r, index) => (
          <Badge key={r.id} tone="purple" size="md">
            {r.name}
            {index === 0 && roles.length > 1 && (
              <span className="font-normal opacity-80"> · {t('tenant.users.roles.primary')}</span>
            )}
          </Badge>
        ))
      )}
      <span className="ml-2 text-text-secondary">
        {t('tenant.permissions.center.subject.team')}:
      </span>
      {teams.length === 0 ? (
        <span className="text-text-muted">
          {t('tenant.permissions.center.companyTabs.noTeams')}
        </span>
      ) : (
        teams.map((tm) => (
          <Badge key={tm.id} tone="success" size="md">
            {tm.name}
          </Badge>
        ))
      )}
      <Link
        to={`/users/${userId}`}
        className="ml-auto inline-flex items-center gap-1 text-accent-600 hover:underline dark:text-accent-400"
        data-testid="subject-company-strip-edit"
      >
        {t('tenant.permissions.center.companyTabs.editOnCard')}
        <ExternalLink size={12} aria-hidden="true" />
      </Link>
    </div>
  );
}
